import { useCallback, useEffect, useMemo, useRef, useState, useContext } from "react";
import {
  useCurrentSimulationResults,
  useFireSimulationPointQuery,
  useSimulations,
  useFireSimulationFrameArrays,
  useFireSimulationMemberArrays,
  useFireSimulationConfidence,
  useFireSimulationMemberPointQuery,
  usePrefetchEnsembleMembers,
  useLoadedEnsembleMembers,
  isMissingFrameArraysError,
} from "../../api/simulations";
import { useUrbanAnimation } from "../../contexts/UrbanAnimationContext";
import { useEnsembleMember } from "../../contexts/EnsembleMemberContext";
import { useSimulation } from "../../contexts/SimulationContext";
import SelectionContext from "../../contexts/SelectionContext"; // Import SelectionContext
import {useDate} from "../../contexts/DateContext"; // Import DateContext
import ToolbarButton from "../toolbar/ToolbarButton";
import ToolbarButtonPopover from "../toolbar/ToolbarButtonPopover";
import dayjs from "dayjs";
import {Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Slider, Tooltip, Typography} from "@mui/material";
import {useTimezone} from "../../contexts/TimezoneContext.tsx";
import WindParticleLayer from "./WindParticleLayer";
import { useProject } from "../../api/project";
import { useIgnitionRegimes } from "../../api/ignitionRegimes";
import { useMap } from "../../contexts/MapContext";
import FireKPIPopover from "../FireKPIPopover";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { bboxFromBoundary } from "../../utils/boundaryUtils";
import { findWeatherGridTimestampIndex, windFieldFromWeatherGrid } from "../../utils/weatherGrid";
import type { WeatherGrid } from "../../types/weatherGrid";
import type { FireView } from "./fire/fireCanvasEngine";
import type { FireKPIData } from "../../types/fireKPI";
import { mapEventScreenPoint } from "../../utils/uiZoom";
import { formatArea } from "../../utils/units";
import { useUnitSystem } from "../../hooks/useUnitSystem";
import {
  renderMetricFrame,
  renderConfidenceFrame,
  boundsToCoordinates,
  isBurnedAt,
  confidencePointStats,
  GRADIENT_PALETTE,
  ISOCHRONE_HOUR_COLORS,
} from "./fire/fireCanvasEngine";

// ---- legacy vector-tile colour expressions (used only for old simulations) ----
const buildGradientStops = (palette: string[], maxVal: number): (number | string)[] => {
  if (!palette.length || maxVal <= 0) return [];
  if (palette.length === 1) return [0, palette[0]];
  return palette.reduce<(number | string)[]>((acc, color, idx) => {
    acc.push((maxVal * idx) / (palette.length - 1), color);
    return acc;
  }, []);
};

const buildGradientColorExpression = (palette: string[], maxVal: number, metricKey: string) => {
  const stops = buildGradientStops(palette, maxVal);
  return stops.length ? ['interpolate', ['linear'], ['number', ['get', metricKey], 0], ...stops] : '#ff0000';
};

const buildIsochroneColorExpression = (maxHour: number, metricKey: string) => {
  const sanitizedMaxHour = Math.max(0, Math.floor(maxHour));
  const colorCount = ISOCHRONE_HOUR_COLORS.length;
  if (!colorCount) return '#ff0000';
  const perHourStops: (number | string)[] = [];
  for (let hour = 1; hour <= sanitizedMaxHour; hour += 1) {
    perHourStops.push(hour, ISOCHRONE_HOUR_COLORS[hour % colorCount]);
  }
  return ['step', ['number', ['get', metricKey], 0], ISOCHRONE_HOUR_COLORS[0], ...perHourStops];
};

// Ensemble members warmed on each side of the scenario slider's position.
const MEMBER_PREFETCH_RADIUS = 2;

interface AnimationLayerProps {
  id: string;
  name: string;
  frameType: FireView;
  disableKpiPopover?: boolean;
}

const AnimationLayer = ({ id, name, frameType, disableKpiPopover = false }: AnimationLayerProps) => {
  const { selectedPolygonId, setSelectedPolygonId } = useContext(SelectionContext);
  const { selectedDate } = useDate();
  const { selectedTimezone } = useTimezone();
  const { data: simResults } = useCurrentSimulationResults();
  const { isSimulationComplete } = useSimulation();
  const results = useMemo(() => simResults?.data?.results ?? [], [simResults?.data?.results]);
  const { user } = useAuth();
  const unitSystem = useUnitSystem();

  const [selectedPartition, setSelectedPartition] = useState<string | null>(null);
  // Ensemble: the clicked ignition POINT (its index within a member's block).
  // Combined with the scenario slider it resolves to one partition, so the
  // same point can be replayed across members. Null = whole-member view.
  const [selectedPointIdx, setSelectedPointIdx] = useState<number | null>(null);
  // Ensemble data is expensive (each member's arrays are a ~100-partition
  // server-side combine), so nothing is fetched when a sim merely loads.
  // `engaged` flips on the first animation interaction — Play, a slider,
  // a metric-view pick, or selecting an ignition — and gates the member /
  // confidence fetches plus the neighbouring-members prefetch.
  const [engaged, setEngaged] = useState(false);
  const [selectedFrameType, setSelectedFrameType] = useState<FireView>(frameType);
  const [frameCount, setFrameCount] = useState<number>(0);
  const [timestamps, setTimestamps] = useState<dayjs.Dayjs[]>([]);
  const [active, setActive] = useState(false);
  const [currentFrame, setCurrentFrame] = useState<number>(0);
  const [stale, setStale] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  // Stop clears the fire off the map. Frame 0 of a polygon ignition is the
  // whole polygon (burnt at t=0), so leaving it drawn covers the ignition
  // underneath and swallows clicks meant for it (GH #432). While stopped the
  // canvas is blank and the layer doesn't claim clicks; any playback
  // interaction or a change of fire brings the frame back.
  const [stopped, setStopped] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [showWind, setShowWind] = useState(false);
  // Live scenario-slider thumb (drives the label) vs the committed position
  // (drives DATA). Mid-drag, the commit follows the thumb only across CACHED
  // members (those render instantly); over cold members it stays put so no
  // fetch fires until the slider is released.
  const [sliderPos, setSliderPos] = useState<number>(0);
  const [committedSliderPos, setCommittedSliderPos] = useState<number>(0);
  const [speedMenuOpen, setSpeedMenuOpen] = useState(false);
  const speedMenuTimeoutRef = useRef<number | null>(null);
  const { map: mapInstance } = useMap();
  const { project } = useProject();
  const [clickedPoint, setClickedPoint] = useState<{ lat: number; lng: number } | null>(null);
  const [clickPopoverPosition, setClickPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const { currentSavedSimId, setIsAnimationPlaying } = useSimulation();
  // Mirror play state into the simulation context so the Risk From layer can
  // step back (dim the other ignitions) while the fire animates.
  useEffect(() => {
    setIsAnimationPlaying(isPlaying);
    return () => setIsAnimationPlaying(false);
  }, [isPlaying, setIsAnimationPlaying]);
  const { data: simulationsData } = useSimulations();
  const savedSimRecord = useMemo(
    () => simulationsData?.data?.find((s) => s.simulation_id === currentSavedSimId) ?? null,
    [simulationsData, currentSavedSimId]
  );
  const { ignitionRegimes } = useIgnitionRegimes(Number(project?.data?.id) || 0);
  const ignitionRegimeName = useMemo(
    () => ignitionRegimes?.find((r) => r.id === savedSimRecord?.ignition_regime_id)?.name,
    [ignitionRegimes, savedSimRecord]
  );

  const rasterCanvasRef = useRef<HTMLCanvasElement | null>(
    typeof document !== "undefined" ? document.createElement("canvas") : null
  );
  const imageDataRef = useRef<ImageData | null>(null);

  const isEnsemble = Boolean(savedSimRecord?.is_ensemble);
  const hasEnsembleFrames = Boolean(savedSimRecord?.has_ensemble_frames);
  const isConfidence = selectedFrameType === "confidence";

  const frameRatePreference = user?.frame_rate_preference || 'low';
  const frameStride = frameRatePreference === 'low' ? 4 : 1;
  // Local frame index -> index into the loaded arrays' frame dimension.
  const actualFrameIndex = currentFrame * frameStride;

  const playbackSpeedMarks = useMemo(() => ([
    { value: 0.5, label: '0.5x' },
    { value: 1, label: '1x' },
    { value: 2, label: '2x' },
    { value: 4, label: '4x' },
  ]), []);

  const { setUrbanAnimation } = useUrbanAnimation();

  // Selecting an ignition (either kind of run) counts as engagement.
  useEffect(() => {
    if (selectedPartition !== null || selectedPointIdx !== null) setEngaged(true);
  }, [selectedPartition, selectedPointIdx]);

  // Selected ignition (non-ensemble: the clicked partition; ensemble: just the
  // first, used for weather + raster-availability).
  const selectedIgnition = results.find(result =>
    result.ignitions?.find(ignition => ignition.partition === selectedPartition)
  );
  const ignitionInfo = selectedIgnition?.ignitions?.find(ignition => ignition.partition === selectedPartition);
  const anyIgnition = results[0]?.ignitions?.[0];

  // Sims produced after compact-array support set has_raster_images; those use
  // the client-side canvas path. Older sims fall back to vector tiles.
  const hasFrameRasters = Boolean(ignitionInfo?.has_raster_images ?? anyIgnition?.has_raster_images);
  const useVectorTiles = !hasFrameRasters;
  const usesArrays = hasFrameRasters;

  // ---- Ensemble member ranking (by total burned area) for the percentile slider ----
  const memberRanking = useMemo(() => {
    const ha = savedSimRecord?.ensemble_stats?.member_burned_area_ha;
    if (!ha || !ha.length) return [] as number[];
    return ha
      .map((v, i) => [v, i] as [number, number])
      .sort((a, b) => a[0] - b[0])
      .map((x) => x[1]);
  }, [savedSimRecord]);
  const memberCount = memberRanking.length || (savedSimRecord?.ensemble_members ?? 0);
  // Default the slider to the median member whenever the ranking changes.
  useEffect(() => {
    if (memberRanking.length) {
      const median = Math.floor(memberRanking.length / 2);
      setSliderPos(median);
      setCommittedSliderPos(median);
    }
  }, [memberRanking.length]);

  // Which members are already in the client cache (reactive; never fetches).
  const loadedMembers = useLoadedEnsembleMembers(
    usesArrays && isEnsemble ? currentSavedSimId : null,
    isEnsemble ? memberCount : 0
  );
  const memberAtPos = (pos: number): number | null =>
    memberRanking.length ? memberRanking[Math.min(pos, memberRanking.length - 1)] : null;

  // Member driving DATA (fetches, partition remap, KPI queries) — derived
  // from the committed position. While dragging, the commit follows the
  // thumb whenever the member under it is CACHED (instant scrub), and stays
  // put over cold members so no fetch fires mid-drag; releasing the slider
  // commits unconditionally, fetching that one member if needed.
  const selectedMemberIdx = isEnsemble ? memberAtPos(committedSliderPos) : null;
  // Member under the live thumb — drives the label so the user sees what
  // they are selecting even before the data is applied on release.
  const thumbMemberIdx = isEnsemble ? memberAtPos(sliderPos) : null;

  // Members are fanned out as contiguous blocks of points_per_member
  // partitions (member m owns idxs [m*ppm, (m+1)*ppm)), so a partition idx
  // maps to its base point as idx % ppm and back as member*ppm + point.
  const pointsPerMember = useMemo(() => {
    const fromStats = savedSimRecord?.ensemble_stats?.points_per_member;
    if (fromStats) return fromStats;
    const total = results[0]?.ignitions?.length ?? 0;
    return memberCount > 0 ? Math.max(1, Math.round(total / memberCount)) : 1;
  }, [savedSimRecord, results, memberCount]);

  // Publish the committed member so consumers outside this layer follow the
  // scenario slider — AssetsLayer recolours its red impacted set from the
  // selected member's precomputed impacted_asset_ids. Gated on `engaged` so
  // merely loading an ensemble doesn't paint the median member's impacts
  // before the user has interacted with the animation at all.
  const { setMemberSelection } = useEnsembleMember();
  useEffect(() => {
    if (isEnsemble && engaged && selectedMemberIdx !== null) {
      setMemberSelection({
        memberIdx: selectedMemberIdx,
        pointsPerMember,
        view: isConfidence ? "confidence" : "member",
      });
    } else {
      setMemberSelection(null);
    }
  }, [
    isEnsemble,
    engaged,
    selectedMemberIdx,
    pointsPerMember,
    isConfidence,
    setMemberSelection,
  ]);
  useEffect(() => () => setMemberSelection(null), [setMemberSelection]);

  // Ensemble: resolve the selected point + slider member to one partition, so
  // moving the slider replays the same ignition point under a different
  // scenario. Clearing the point returns to the whole-member view.
  useEffect(() => {
    if (!isEnsemble) return;
    if (selectedPointIdx === null || selectedMemberIdx === null) {
      setSelectedPartition(null);
      return;
    }
    const targetIdx = selectedMemberIdx * pointsPerMember + selectedPointIdx;
    const match = (results[0]?.ignitions ?? []).find(
      (ig) => parseInt(String(ig.partition).split("_")[0], 10) === targetIdx
    );
    setSelectedPartition(match?.partition ?? null);
  }, [isEnsemble, selectedPointIdx, selectedMemberIdx, pointsPerMember, results]);

  // ---- Data: raw arrays for the active scenario ----
  // Partition arrays load whenever a partition is resolved — single runs from
  // the clicked ignition, ensembles from the clicked point + slider member.
  const { data: partitionArrays, isFetching: partitionArraysFetching, error: partitionArraysError } = useFireSimulationFrameArrays(
    usesArrays ? currentSavedSimId : null,
    usesArrays ? selectedPartition : null
  );
  // A legacy simulation without compact frame arrays can never be played
  // back — tell the user it needs a rerun and drop the failed selection so
  // the animation UI doesn't sit waiting on data that will never arrive.
  const [outdatedDialogOpen, setOutdatedDialogOpen] = useState(false);
  useEffect(() => {
    if (!isMissingFrameArraysError(partitionArraysError)) return;
    setOutdatedDialogOpen(true);
    setIsPlaying(false);
    setSelectedPartition(null);
    setSelectedPointIdx(null);
    setSelectedPolygonId(null);
  }, [partitionArraysError, setSelectedPolygonId]);

  const { data: memberArrays, isFetching: memberArraysFetching } = useFireSimulationMemberArrays(
    usesArrays && isEnsemble && !isConfidence && engaged ? currentSavedSimId : null,
    usesArrays && isEnsemble && !isConfidence && engaged ? selectedMemberIdx : null
  );
  // Loaded for the Confidence view AND for the member-proportion line in the
  // metric-view popover (cached per point, fetched once). With an ignition
  // point selected the stack is scoped to that point's fires across members;
  // otherwise it covers the whole scenario.
  const { data: confidenceArrays, isFetching: confidenceArraysFetching } = useFireSimulationConfidence(
    currentSavedSimId,
    usesArrays && isEnsemble && hasEnsembleFrames && engaged,
    selectedPointIdx
  );

  // Ensemble with a clicked point → play that partition alone; otherwise the
  // member view (all of the scenario's ignitions) as before.
  const isMemberView = isEnsemble && !isConfidence && !selectedPartition;
  // Warm the members either side of the slider in the background (nearest
  // first) so stepping the scenario slider hits the cache. Only once the
  // whole-member view is actually in use — with an ignition selected,
  // scrubbing fetches single partitions instead, which are cheap.
  const prefetchMembers = useMemo(() => {
    const out: number[] = [];
    for (let d = 1; d <= MEMBER_PREFETCH_RADIUS; d++) {
      for (const pos of [committedSliderPos + d, committedSliderPos - d]) {
        if (pos >= 0 && pos < memberRanking.length) out.push(memberRanking[pos]);
      }
    }
    return out;
  }, [memberRanking, committedSliderPos]);
  usePrefetchEnsembleMembers(
    currentSavedSimId,
    prefetchMembers,
    usesArrays && isMemberView && engaged
  );
  const activeFrameArrays = isMemberView ? memberArrays ?? null : partitionArrays ?? null;
  const activeBounds = (isConfidence ? confidenceArrays?.bounds : activeFrameArrays?.bounds) ?? null;
  const activeShape = (isConfidence ? confidenceArrays?.shape : activeFrameArrays?.shape) ?? null;
  const arraysReady = isConfidence ? !!confidenceArrays : !!activeFrameArrays;
  // Any in-flight fetch for the view being rendered — drives the toolbar
  // spinner so ignition clicks and cold member/confidence loads show progress
  // (the canvas keeps the previous data on screen while these resolve).
  const arraysLoading = isConfidence
    ? confidenceArraysFetching
    : isMemberView
      ? memberArraysFetching
      : partitionArraysFetching;

  // KPI popover (point query). Partition views (single runs, or an ensemble's
  // clicked point) query the partition; ensemble member views query the
  // slider-selected member (its partitions combined).
  const { data: partitionKpi, isLoading: partitionKpiLoading } = useFireSimulationPointQuery(
    useVectorTiles || isConfidence ? null : currentSavedSimId,
    selectedPartition,
    clickedPoint?.lat ?? null,
    clickedPoint?.lng ?? null,
    actualFrameIndex
  );
  const { data: memberKpi, isLoading: memberKpiLoading } = useFireSimulationMemberPointQuery(
    isMemberView ? currentSavedSimId : null,
    isMemberView ? selectedMemberIdx : null,
    clickedPoint?.lat ?? null,
    clickedPoint?.lng ?? null,
    actualFrameIndex
  );

  const frameElapsedS = useMemo(() => {
    if (!timestamps.length || currentFrame >= timestamps.length) return null;
    return timestamps[currentFrame].diff(timestamps[0], 'second');
  }, [timestamps, currentFrame]);

  // Across-member stats at the clicked point (burn-probability + arrival band),
  // read directly from the confidence stack — used by both the Confidence-view
  // popover and the member-view popover's "members reaching here" line.
  const confidenceStats = useMemo(() => {
    if (!clickedPoint || !confidenceArrays) return null;
    return confidencePointStats(
      confidenceArrays,
      clickedPoint.lng,
      clickedPoint.lat,
      frameElapsedS ?? Number.POSITIVE_INFINITY
    );
  }, [clickedPoint, confidenceArrays, frameElapsedS]);

  // KPI payload for the popover, per view.
  const kpiData = useMemo(() => {
    if (isConfidence) {
      if (!confidenceStats) return null;
      const data: FireKPIData = {
        timestamp: null,
        fire_size_ha: null,
        assets_impacted: null,
        fireline_intensity_kw_m: null,
        flame_height_m: null,
        rate_of_spread_km_h: null,
        member_burn_probability: confidenceStats.probNow,
        member_burn_probability_final: confidenceStats.probFinal,
        ensemble_members: savedSimRecord?.ensemble_members ?? null,
        arrival_time_hours: confidenceStats.p50ArrivalSec != null ? confidenceStats.p50ArrivalSec / 3600 : null,
        arrival_p10_hours: confidenceStats.p10ArrivalSec != null ? confidenceStats.p10ArrivalSec / 3600 : null,
        arrival_p90_hours: confidenceStats.p90ArrivalSec != null ? confidenceStats.p90ArrivalSec / 3600 : null,
      };
      return { data };
    }
    if (isMemberView) {
      if (!memberKpi?.data) return memberKpi;
      // Augment the member's point KPIs with the ensemble proportion at this cell.
      return {
        ...memberKpi,
        data: {
          ...memberKpi.data,
          member_burn_probability: confidenceStats?.probNow ?? null,
          member_burn_probability_final: confidenceStats?.probFinal ?? null,
          ensemble_members: savedSimRecord?.ensemble_members ?? null,
        },
      };
    }
    return partitionKpi;
  }, [isConfidence, isMemberView, confidenceStats, memberKpi, partitionKpi, savedSimRecord]);
  const kpiLoading = isConfidence ? false : isMemberView ? memberKpiLoading : partitionKpiLoading;

  // Urban layers read their data off `selectedIgnitionInfo`. The collated
  // results list no longer carries the per-ignition urban fields (they made
  // ensemble payloads hundreds of MB), so single runs use the urban fields the
  // frame-arrays endpoint returns for the clicked partition, and ensemble
  // member views use the merged urban fields the member-arrays endpoint
  // returns for the slider-selected member. The Confidence view has no single
  // member, so it carries no urban data (null) — there's nothing to animate.
  // `ignitionInfo` remains as a fallback for old vector-tile sims whose cached
  // results predate the slimming.
  const urbanIgnitionInfo = isMemberView
    ? memberArrays?.urban ?? null
    : partitionArrays?.urban ?? ignitionInfo ?? null;
  useEffect(() => {
    setUrbanAnimation({ frameElapsedS, selectedIgnitionInfo: urbanIgnitionInfo, currentFrame, timestamps });
  }, [urbanIgnitionInfo, currentFrame, timestamps]);

  // ---- Timeline: derive timestamps from the loaded arrays' frame_times ----
  useEffect(() => {
    const ft = isConfidence
      ? confidenceArrays?.frameTimesSeconds
      : activeFrameArrays?.frameTimesSeconds;
    const startStr = results[0]?.start_date_time;
    if (ft && ft.length && startStr) {
      const start = dayjs(startStr);
      const all = ft.map((s) => start.add(s, "second"));
      const filtered = all.filter((_, i) => i % frameStride === 0);
      setFrameCount(filtered.length);
      setTimestamps(filtered);
      setStale(true);
    } else {
      setFrameCount(0);
      setTimestamps([]);
    }
  }, [activeFrameArrays, confidenceArrays, isConfidence, frameStride, results]);

  // Clamp the current frame when the timeline shrinks (e.g. switching views).
  useEffect(() => {
    if (frameCount > 0 && currentFrame >= frameCount) setCurrentFrame(frameCount - 1);
  }, [frameCount, currentFrame]);

  // ---- Canvas: (re)allocate ImageData when the grid changes ----
  const shapeH = activeShape?.[0];
  const shapeW = activeShape?.[1];
  useEffect(() => {
    const canvas = rasterCanvasRef.current;
    if (!canvas || !shapeH || !shapeW) return;
    if (canvas.width !== shapeW || canvas.height !== shapeH) {
      canvas.width = shapeW;
      canvas.height = shapeH;
    }
    const ctx = canvas.getContext("2d");
    imageDataRef.current = ctx ? ctx.createImageData(shapeW, shapeH) : null;
  }, [shapeH, shapeW]);

  // ---- Canvas: draw the current frame client-side from arrays ----
  useEffect(() => {
    if (useVectorTiles) return;
    const canvas = rasterCanvasRef.current;
    const img = imageDataRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (stopped) {
      img.data.fill(0);
    } else if (isConfidence) {
      if (!confidenceArrays) return;
      renderConfidenceFrame(img, confidenceArrays, actualFrameIndex);
    } else {
      if (!activeFrameArrays) return;
      renderMetricFrame(img, selectedFrameType as Exclude<FireView, "confidence">, activeFrameArrays, actualFrameIndex);
    }
    ctx.putImageData(img, 0, 0);
    setStale(true);
  }, [useVectorTiles, isConfidence, activeFrameArrays, confidenceArrays, actualFrameIndex, selectedFrameType, stopped]);

  const canvasSource = useMemo(() => {
    if (!rasterCanvasRef.current || !activeBounds) return null;
    return {
      type: "canvas" as const,
      canvas: rasterCanvasRef.current,
      animate: true,
      coordinates: boundsToCoordinates(activeBounds),
    };
  }, [activeBounds]);

  // ---- Activation ----
  useEffect(() => {
    // Ensemble activates as soon as members are known; single runs activate on
    // a selected ignition partition.
    if (isEnsemble) {
      setActive(usesArrays && memberCount > 0);
    } else {
      setActive(!!selectedPartition);
    }
  }, [isEnsemble, usesArrays, memberCount, selectedPartition]);

  useEffect(() => {
    if (!selectedPartition && !isEnsemble) return;
    setStale(false);
    const frameId = requestAnimationFrame(() => setStale(true));
    return () => cancelAnimationFrame(frameId);
  }, [selectedPartition, selectedFrameType, useVectorTiles, isEnsemble, selectedMemberIdx]);

  // A different fire, view or scenario member shows its frame 0 again.
  useEffect(() => {
    setStopped(false);
  }, [selectedPartition, selectedFrameType, selectedMemberIdx]);

  // Reset all animation state when the loaded simulation changes.
  useEffect(() => {
    setIsPlaying(false);
    setStopped(false);
    setActive(false);
    setCurrentFrame(0);
    setFrameCount(0);
    setTimestamps([]);
    setSelectedPartition(null);
    setSelectedPointIdx(null);
    setEngaged(false);
    setSelectedPolygonId(null);
    setClickedPoint(null);
    setClickPopoverPosition(null);
    setSelectedFrameType(frameType);
    setStale(true);
  }, [currentSavedSimId, setSelectedPolygonId, frameType]);

  // Pick the fire from the clicked ignition polygon. Single runs select the
  // partition directly; ensembles select the base POINT (partition idx modulo
  // points_per_member — the stacked per-member squares at one location all
  // resolve to the same point), which the slider member maps to a partition.
  useEffect(() => {
    const setFromIgnition = (ignition: { partition?: string } | null) => {
      if (isEnsemble) {
        const idx = ignition?.partition != null
          ? parseInt(String(ignition.partition).split("_")[0], 10)
          : NaN;
        setSelectedPointIdx(Number.isNaN(idx) ? null : idx % pointsPerMember);
      } else {
        setSelectedPartition(ignition?.partition ?? null);
      }
    };
    if (
      results.length > 0 &&
      isSimulationComplete &&
      selectedPolygonId !== null &&
      selectedDate &&
      /^ignition-\d+$/.test(selectedPolygonId)
    ) {
      const selectedDateOnly = selectedDate.format("YYYY-MM-DD");
      const ignitionDataForDate = results.find(
        (data) => dayjs(data.start_date_time).tz(selectedTimezone).format("YYYY-MM-DD") === selectedDateOnly
      );
      if (ignitionDataForDate) {
        const index = parseInt(selectedPolygonId.split("-")[1], 10);
        setFromIgnition(ignitionDataForDate.ignitions[index] ?? null);
      } else {
        setFromIgnition(null);
      }
    } else if (!selectedPartition) {
      setSelectedPartition(null);
    }
    // Clicking away from an ignition keeps the current selection (both single
    // runs and ensembles) — only clicking a different ignition, or loading
    // another simulation, changes the fire being played.
  }, [results, isSimulationComplete, selectedPolygonId, selectedDate, isEnsemble, pointsPerMember]);

  // ---- Playback: all frames are local, so just advance on a timer ----
  // Above 1x the tick stays at the base rate and SKIPS frames instead of
  // shortening the delay: a 12 ms timer can't outrun the canvas redraw, so
  // 4x used to look much like 1x (GH #393). Below 1x the delay stretches.
  // The last frame is always landed on before the loop wraps.
  useEffect(() => {
    if (!active || !isPlaying || frameCount < 2 || !arraysReady) return;
    const baseDelayMs = 50;
    const step = Math.max(1, Math.round(playbackSpeed));
    const delayMs = playbackSpeed < 1 ? baseDelayMs / playbackSpeed : baseDelayMs;
    const id = window.setTimeout(() => {
      setCurrentFrame((f) => {
        const last = frameCount - 1;
        if (f >= last) return 0;
        return Math.min(f + step, last);
      });
      setStale(true);
    }, delayMs);
    return () => window.clearTimeout(id);
  }, [active, isPlaying, frameCount, arraysReady, currentFrame, playbackSpeed]);

  const handlePlay = () => {
    setEngaged(true);
    setStopped(false);
    setIsPlaying(true);
    setActive(true);
  };
  const handlePause = () => setIsPlaying(false);
  // One button plays and pauses (GH #393).
  const handlePlayPause = () => { if (isPlaying) handlePause(); else handlePlay(); };
  // Clicking the speed chip steps through the marks; hovering it opens the
  // slider for finer control.
  const cyclePlaybackSpeed = () => {
    const steps = playbackSpeedMarks.map((m) => m.value);
    // Next mark above the current rate (the slider allows in-between values).
    setPlaybackSpeed(steps.find((v) => v > playbackSpeed) ?? steps[0]);
  };
  // Delayed hide (matching the sidebar popovers) so the speed menu survives the
  // mouse crossing the gap between the speed chip and the menu.
  const openSpeedMenu = () => {
    if (speedMenuTimeoutRef.current) {
      window.clearTimeout(speedMenuTimeoutRef.current);
      speedMenuTimeoutRef.current = null;
    }
    setSpeedMenuOpen(true);
  };
  const scheduleSpeedMenuClose = () => {
    speedMenuTimeoutRef.current = window.setTimeout(() => setSpeedMenuOpen(false), 300);
  };
  useEffect(() => () => {
    if (speedMenuTimeoutRef.current) window.clearTimeout(speedMenuTimeoutRef.current);
  }, []);
  const handleStop = () => {
    setIsPlaying(false);
    setStopped(true);
    setCurrentFrame(0);
    setClickedPoint(null);
    setClickPopoverPosition(null);
    setStale(true);
  };
  const handleToggleWind = () => setShowWind((s) => !s);

  // Wind data for the current frame (weather is shared across members).
  const weatherSource = selectedIgnition ?? results[0];
  const currentWindData = useMemo(() => {
    if (!timestamps[currentFrame]) return { windSpeed: 0, windDirection: 0 };
    const weather = (weatherSource as any)?.weather;
    if (!weather) return { windSpeed: 0, windDirection: 0 };
    const weatherData = weather.interval_weather || weather;
    for (const [timestamp, data] of Object.entries(weatherData)) {
      const weatherTime = dayjs(timestamp);
      if (weatherTime.isSame(timestamps[currentFrame], 'hour')) {
        const hourData = data as any;
        return { windSpeed: hourData.wind_speed || 0, windDirection: hourData.wind_direction || 0 };
      }
    }
    return { windSpeed: 0, windDirection: 0 };
  }, [currentFrame, timestamps, weatherSource]);

  // Per-point wind for the current frame when the run carried a weather grid.
  // The results entry is built from the simulation record (like `weather`
  // above); the saved-list record is a fallback for responses that carry it.
  // Null (legacy / uniform runs) leaves the particle layer on the scalar above.
  const currentWindField = useMemo(() => {
    const frameTime = timestamps[currentFrame];
    if (!frameTime) return null;
    const grid: WeatherGrid | null = weatherSource?.weather_grid ?? savedSimRecord?.weather_grid ?? null;
    if (!grid?.timestamps?.length || !grid.vars) return null;
    const t = findWeatherGridTimestampIndex(grid, frameTime);
    if (t < 0) return null;
    return windFieldFromWeatherGrid(grid, t);
  }, [currentFrame, timestamps, weatherSource, savedSimRecord]);

  // Point hit-test. Single runs + member views use the arrival array; the
  // Confidence view tests whether any member has reached the point BY THE
  // CURRENT FRAME (probNow, not probFinal) so the clickable footprint tracks
  // the timeline like the other views — at frame 0 (stopped/reset) nothing is
  // reached yet, so clicks don't open the popover.
  const isPointInActiveFire = useCallback((lng: number, lat: number) => {
    if (useVectorTiles || stopped) return false;
    if (isConfidence) {
      if (!confidenceArrays) return false;
      const s = confidencePointStats(confidenceArrays, lng, lat, frameElapsedS ?? Number.POSITIVE_INFINITY);
      return !!s && s.probNow > 0;
    }
    if (!activeFrameArrays) return false;
    const tSec = activeFrameArrays.frameTimesSeconds[actualFrameIndex] ?? Number.POSITIVE_INFINITY;
    return isBurnedAt(activeFrameArrays, lng, lat, tSec);
  }, [useVectorTiles, stopped, isConfidence, confidenceArrays, frameElapsedS, activeFrameArrays, actualFrameIndex]);

  const displayName = selectedFrameType === 'rate_of_spread' ? 'Rate of Spread'
    : selectedFrameType === 'fireline_intensity' ? 'Fireline Intensity'
    : selectedFrameType === 'flame_height' ? 'Flame Height'
    : selectedFrameType === 'isochrones' ? 'Isochrones'
    : selectedFrameType === 'confidence' ? 'Confidence'
    : name;

  // ---- Legacy vector-tile fallback (old sims only) ----
  let tilesUrl: string | undefined;
  let sourceLayer: string | undefined;
  if (ignitionInfo && useVectorTiles) {
    switch (selectedFrameType) {
      case 'rate_of_spread': tilesUrl = ignitionInfo.ros_tiles_url; sourceLayer = 'fire-ros'; break;
      case 'fireline_intensity': tilesUrl = ignitionInfo.intensity_tiles_url; sourceLayer = 'fire-intensity'; break;
      case 'flame_height': tilesUrl = ignitionInfo.flame_tiles_url; sourceLayer = 'fire-flame'; break;
      case 'isochrones': tilesUrl = ignitionInfo.iso_tiles_url; sourceLayer = 'fire-isochrone'; break;
      default: tilesUrl = undefined; sourceLayer = undefined;
    }
  }
  const vectorSource = useMemo(() => {
    if (!tilesUrl || !useVectorTiles) return null;
    return { type: "vector" as const, tiles: [tilesUrl], tileSize: 512, minzoom: 6, maxzoom: 14, promoteId: "__id" };
  }, [tilesUrl, useVectorTiles]);
  const maxVal = selectedFrameType === 'rate_of_spread' ? (ignitionInfo?.max_ros ?? 1)
    : selectedFrameType === 'fireline_intensity' ? (ignitionInfo?.max_intensity ?? 1)
    : selectedFrameType === 'flame_height' ? (ignitionInfo?.max_flame_height ?? 1)
    : selectedFrameType === 'isochrones' ? (ignitionInfo?.max_hour ?? 1)
    : 1;
  const colorExpression = selectedFrameType === 'isochrones'
    ? buildIsochroneColorExpression(maxVal, 'value')
    : buildGradientColorExpression(GRADIENT_PALETTE, maxVal, 'value');

  const layerStyle = useVectorTiles ? {
    id: id,
    type: 'fill',
    'source-layer': sourceLayer ?? '',
    filter: ['==', ['get', 'frame_number'], actualFrameIndex],
    paint: { 'fill-color': colorExpression, 'fill-opacity': 0.85 },
    layout: { visibility: active ? 'visible' : 'none' },
  } : {
    id: id,
    type: 'raster',
    paint: { 'raster-opacity': isConfidence ? 1 : 0.85, 'raster-fade-duration': 0, 'raster-resampling': 'nearest' },
    layout: { visibility: active ? 'visible' : 'none' },
  };

  const metricButtonsEnabled = isEnsemble ? active : !!selectedPartition;

  const frameTypeOptions = useMemo(() => {
    const options: { key: FireView; label: string; icon: string }[] = [
      { key: 'rate_of_spread', label: 'Rate of Spread', icon: 'speed' },
      { key: 'fireline_intensity', label: 'Fireline Intensity', icon: 'whatshot' },
      { key: 'flame_height', label: 'Flame Height', icon: 'height' },
      { key: 'isochrones', label: 'Isochrones', icon: 'schedule' },
    ];
    if (isEnsemble && hasEnsembleFrames) {
      options.push({ key: 'confidence', label: 'Confidence', icon: 'blur_on' });
    }
    return options;
  }, [isEnsemble, hasEnsembleFrames]);

  // ---- Ensemble percentile-slider label ----
  const percentilePct = memberCount > 1 ? Math.round((sliderPos / (memberCount - 1)) * 100) : 50;
  const scenarioHa = useMemo(() => {
    const ha = savedSimRecord?.ensemble_stats?.member_burned_area_ha;
    if (!ha || thumbMemberIdx == null || thumbMemberIdx >= ha.length) return null;
    return Math.round(ha[thumbMemberIdx]);
  }, [savedSimRecord, thumbMemberIdx]);

  const controls = (
    <Box sx={{ display: "flex", height: "100%", alignItems: "center", flexDirection: "row", ml: 5 }}>
      {/* Playback speed lives on its own chip (Spotify-style) rather than
          hiding behind the Play button: the chip shows the current rate,
          a click steps to the next one, and hovering opens the slider. */}
      <Box
        sx={{ position: "relative", display: "flex", alignItems: "center" }}
        onMouseEnter={openSpeedMenu}
        onMouseLeave={scheduleSpeedMenuClose}
      >
        <Box
          className="animation-speed-control"
          sx={{
            position: "absolute", left: "50%", bottom: "calc(100% + 8px)",
            transform: speedMenuOpen ? "translate(-50%, 0)" : "translate(-50%, 8px)",
            opacity: speedMenuOpen ? 1 : 0, pointerEvents: speedMenuOpen ? "auto" : "none",
            transition: "opacity 150ms ease, transform 150ms ease",
            backgroundColor: "rgba(15, 23, 42, 0.95)", borderRadius: 1, px: 1.5, py: 1, zIndex: 10,
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.35)", display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: "white", whiteSpace: "nowrap" }}>{`Speed ${playbackSpeed}x`}</Typography>
          <Slider
            orientation="vertical" value={playbackSpeed} min={0.5} max={4} step={0.5} marks={playbackSpeedMarks}
            disabled={frameCount === 0}
            sx={{
              height: 140,
              "& .MuiSlider-markLabel": { color: "white", fontSize: "0.625rem", transform: "translateX(30px)" },
              "& .MuiSlider-track": { backgroundColor: "#00d4ad", border: "none" },
              "& .MuiSlider-thumb": { backgroundColor: frameCount > 0 ? "#00d4ad" : "gray" },
              "& .MuiSlider-rail": { backgroundColor: frameCount > 0 ? "#00d4ad" : "gray" },
              "& .MuiSlider-mark": { backgroundColor: "white" },
            }}
            onChange={(_event, newValue) => { if (typeof newValue === "number") setPlaybackSpeed(newValue); }}
            valueLabelDisplay="off"
          />
        </Box>
        <ToolbarButton
          key="speed" id="speed" name="Speed" toggledOn={true} enabled={frameCount > 0}
          onClick={cyclePlaybackSpeed} labelSide={"top"}
          icon={(
            <Box
              component="span"
              sx={{
                width: 24, height: 24, display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontSize: "0.75rem", fontWeight: 700, lineHeight: 1, letterSpacing: "-0.02em",
              }}
            >
              {`${playbackSpeed}x`}
            </Box>
          )}
        />
      </Box>
      {/* Pre-engagement there are no frames yet (nothing is fetched until
          the user interacts), so Play must stay clickable for an ensemble:
          clicking it starts the load and playback begins when data lands. */}
      <ToolbarButton
        key="play-pause" id="play-pause" name={isPlaying ? "Pause" : "Play"} toggledOn={true}
        enabled={frameCount > 0 || (isEnsemble && usesArrays && !engaged)}
        onClick={handlePlayPause} icon={isPlaying ? "pause" : "play_arrow"} labelSide={"top"}
      />
      <ToolbarButton key="stop" id="stop" name="Stop" toggledOn={true} enabled={frameCount > 0} onClick={handleStop} icon={"stop"} labelSide={"top"} />
      <ToolbarButton key="wind" id="wind" name="Wind" toggledOn={showWind} enabled={frameCount > 0} onClick={handleToggleWind} icon={"air"} labelSide={"top"} />
      <Slider
        value={currentFrame} min={0} max={Math.max(frameCount - 1, 0)}
        sx={{
          width: "100px", ml: 3, mr: 3,
          "&.MuiSlider-root": { height: 10 },
          "& .MuiSlider-track": { backgroundColor: "#00d4ad" },
          "& .MuiSlider-thumb": { backgroundColor: active ? "#00d4ad" : "gray" },
          "& .MuiSlider-rail": { backgroundColor: active ? "#00d4ad" : "gray" },
        }}
        onChange={(_event, newValue) => {
          if (typeof newValue === "number") { setEngaged(true); setStopped(false); setCurrentFrame(newValue); setStale(true); }
        }}
        valueLabelDisplay="off"
      />
      <Tooltip title={`Timezone: ${selectedTimezone}`} arrow placement="top">
        <Typography
          variant="body1"
          sx={{
            ml: 1, mr: 1, color: "white", minWidth: "220px", textAlign: "left", alignSelf: "center",
            display: "flex", alignItems: "center", height: "100%", cursor: 'help',
          }}
        >
          {timestamps[currentFrame] ? timestamps[currentFrame].tz(selectedTimezone).format("D MMM YYYY, h:mm:ss A") : ""}
        </Typography>
      </Tooltip>

      {/* Data-loading indicator: fires while the selected ignition's arrays,
          a cold ensemble member, or a point-scoped confidence stack loads.
          Rendered on a dark chip (matching the speed menu) so it reads
          against the map underneath. */}
      <Box sx={{ width: 36, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {arraysLoading && (
          <Tooltip title="Loading fire data…" arrow placement="top">
            <Box
              sx={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 30, height: 30, borderRadius: "50%",
                backgroundColor: "rgba(15, 23, 42, 0.95)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.45)",
              }}
            >
              <CircularProgress size={18} thickness={5} sx={{ color: "#00d4ad" }} />
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Frame-type selector */}
      <Box sx={{ ml: 3, display: "flex", gap: 0.5 }}>
        <ToolbarButtonPopover
          id="frame-type"
          name={displayName}
          enabled={metricButtonsEnabled}
          toggledOn={true}
          icon={frameTypeOptions.find((o) => o.key === selectedFrameType)?.icon ?? 'layers'}
          labelSide="top"
          openOnHover
          options={frameTypeOptions.map((o) => ({
            id: o.key,
            label: o.label,
            icon: o.icon,
            selected: selectedFrameType === o.key,
            onClick: () => { setEngaged(true); setSelectedFrameType(o.key); setStale(true); },
          }))}
        />
      </Box>

      {/* Ensemble percentile (scenario) slider — metric views only. On a dark
          chip (matching the speed menu / spinner) so it reads over the map. */}
      {isEnsemble && !isConfidence && memberCount > 1 && (
        <Box
          sx={{
            ml: 3, display: "flex", flexDirection: "column", minWidth: 180,
            backgroundColor: "rgba(15, 23, 42, 0.9)", borderRadius: 1.5,
            px: 1.5, py: 0.5, boxShadow: "0 2px 8px rgba(0, 0, 0, 0.45)",
          }}
        >
          <Typography variant="caption" sx={{ color: "white", whiteSpace: "nowrap" }}>
            {`Scenario P${percentilePct}${scenarioHa != null ? ` · avg ${formatArea(scenarioHa, unitSystem)}` : ''}`}
          </Typography>
          <Slider
            value={sliderPos} min={0} max={memberCount - 1} step={1}
            sx={{
              "& .MuiSlider-track": { backgroundColor: "#a78bfa" },
              "& .MuiSlider-thumb": { backgroundColor: "#a78bfa" },
              "& .MuiSlider-rail": { backgroundColor: "rgba(167,139,250,0.4)" },
            }}
            onChange={(_event, v) => {
              if (typeof v !== "number") return;
              setEngaged(true);
              setSliderPos(v);
              // Mid-drag, apply the position only when its member is cached.
              const m = memberAtPos(v);
              if (m !== null && loadedMembers[m]) setCommittedSliderPos(v);
              setStale(true);
            }}
            onChangeCommitted={(_event, v) => {
              if (typeof v !== "number") return;
              setEngaged(true);
              setSliderPos(v);
              setCommittedSliderPos(v);
              setStale(true);
            }}
            valueLabelDisplay="off"
          />
        </Box>
      )}
    </Box>
  );

  // Map clicks for KPI queries (canvas path; single runs, members + confidence).
  const handleMapClick = (e: any) => {
    const canQuery = isConfidence ? !!confidenceArrays : isMemberView ? selectedMemberIdx != null : !!selectedPartition;
    if (!active || !canQuery || frameCount === 0 || useVectorTiles || disableKpiPopover) return false;
    if (!isPointInActiveFire(e.lngLat.lng, e.lngLat.lat)) {
      handleClosePopover();
      return false;
    }
    setIsPlaying(false);
    setClickedPoint({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    setClickPopoverPosition(mapEventScreenPoint(e));
    return true;
  };

  const handleClosePopover = () => {
    setClickedPoint(null);
    setClickPopoverPosition(null);
  };

  useEffect(() => {
    if (disableKpiPopover) handleClosePopover();
  }, [disableKpiPopover]);

  // Switching views invalidates the per-view query — close any open popover.
  useEffect(() => {
    handleClosePopover();
  }, [selectedFrameType]);

  const showKpiPopover = !disableKpiPopover && Boolean(clickPopoverPosition) && !kpiData?.data?.message;

  const currentSource = useVectorTiles ? vectorSource : canvasSource;

  const outdatedDialog = (
    <Dialog
      open={outdatedDialogOpen}
      onClose={() => setOutdatedDialogOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { bgcolor: '#1a1d2e', color: '#fff' } }}
    >
      <DialogTitle sx={{ color: '#fff' }}>Simulation Outdated</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          This simulation was run with an older version of Mitigate and its playback data is no
          longer available. Please rerun the simulation to view its animation — use the rerun
          button in the Saved Simulations menu.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setOutdatedDialogOpen(false)}
          variant="contained"
          sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#ff7f50' } }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (!currentSource) {
    // No source yet (e.g. the failed legacy fetch above) — still surface the
    // outdated dialog, which mounts through the customComponent slot.
    return {
      id, name: displayName, source: null, layerStyle: [], controls,
      active: false, stale, setStale,
      customComponent: outdatedDialog,
    };
  }

  return {
    id,
    name: displayName,
    type: useVectorTiles ? 'vector' : 'raster',
    source: currentSource,
    layerStyle: [layerStyle],
    controls,
    active,
    stale,
    setStale,
    onClick: handleMapClick,
    interactiveLayerIds: active ? [id] : [],
    customComponent: (
      <>
        <WindParticleLayer
          map={mapInstance}
          isActive={showWind && active}
          windSpeed={currentWindData.windSpeed}
          windDirection={currentWindData.windDirection}
          field={currentWindField}
          bounds={(() => {
            const bbox = bboxFromBoundary(project?.data?.boundary);
            return bbox ?? { top: 0, bottom: 0, left: 0, right: 0 };
          })()}
        />
        {outdatedDialog}
      </>
    ),
    popover: showKpiPopover ? (
      <FireKPIPopover
        anchorPosition={clickPopoverPosition}
        onClose={handleClosePopover}
        kpis={kpiData?.data ?? null}
        timestamp={timestamps[currentFrame] ?? null}
        loading={kpiLoading}
        simulationId={currentSavedSimId}
        partition={selectedPartition}
        frame={actualFrameIndex}
        projectName={project?.data?.name}
        strategyName={savedSimRecord?.mitigation_strategy_name}
        ignitionRegimeName={ignitionRegimeName}
        simulationName={savedSimRecord?.name}
      />
    ) : null,
  };
};

export default AnimationLayer;
